import {
  formatDateTime,
  MAIL_DRIVER_LABELS,
  SMTP_PRESETS,
  ZOHO_REGIONS,
  ZOHO_REGION_KEYS,
  type MailConfigResponse,
  type MailDriver,
  type ZohoRegion,
} from "@nexseed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  CheckCircle2,
  Mail,
  Pencil,
  Send,
  Trash2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/app/dialogs";
import { EmptyState } from "@/components/app/empty-state";
import { PageHeader } from "@/components/app/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip } from "@/components/ui/tooltip";
import { useAuth } from "@/features/auth/store";
import { useFacilities, type Facility } from "@/features/auth/use-facilities";
import { api, ApiError } from "@/lib/api-client";
import { applyApiError } from "@/lib/form-errors";

export const Route = createFileRoute("/_app/masters/mail-settings")({
  beforeLoad: () => {
    if (!useAuth.getState().can("mail_settings.view"))
      throw redirect({ to: "/" });
  },
  component: Page,
});

const KEY = ["masters", "mail-settings"] as const;

/** Short names for the table; the form spells them out. */
const DRIVER_SHORT: Record<MailDriver, string> = {
  zoho_api: "Zoho API",
  gmail_api: "Gmail API",
  smtp: "SMTP",
};

/**
 * Masters → Mail Settings (user, 2026-10-15): each processing plant's one mail account. A plant's
 * invites, login codes, reset codes and arrival e-mails go out through it; a plant without one
 * cannot send mail at all.
 */
function Page() {
  const qc = useQueryClient();
  const manage = useAuth((s) => s.can("mail_settings.manage"));
  const { facilities } = useFacilities();
  const plants = facilities.filter((f) => f.type !== "external");
  const list = useQuery({
    queryKey: KEY,
    queryFn: () =>
      api<{ items: MailConfigResponse[] }>("/masters/mail-settings"),
    select: (d) => d.items,
  });
  const byPlant = new Map((list.data ?? []).map((c) => [c.facilityId, c]));
  const [editing, setEditing] = useState<{
    plant: Facility;
    config: MailConfigResponse | null;
  } | null>(null);
  const [testing, setTesting] = useState<MailConfigResponse | null>(null);
  const [deleting, setDeleting] = useState<MailConfigResponse | null>(null);

  const remove = useMutation({
    mutationFn: (c: MailConfigResponse) =>
      api<void>(`/masters/mail-settings/${c.id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success("Mail settings removed");
      setDeleting(null);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not remove"),
  });

  return (
    <>
      <PageHeader
        title="Mail Settings"
        description="Each processing plant's own mail account. Invites, login codes, password-reset codes and arrival e-mails go out from the plant they belong to. A plant with no account here cannot send mail."
      />
      <Callout tone="info" className="mb-4">
        This server cannot reach SMTP ports, so use <b>Zoho Mail (API)</b> or{" "}
        <b>Gmail (API)</b> here. SMTP (Gmail app password / Zoho SMTP) only
        works on a host that allows outgoing mail ports.
      </Callout>
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Plants</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {plants.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No plants"
              description="You are not assigned to any processing plant."
              compact
            />
          ) : (
            <Table>
              <THead className="bg-surface-header text-xs font-bold tracking-normal text-ink-muted normal-case [&_th]:h-9 [&_th]:border-b [&_th]:border-border">
                <tr>
                  <TH>Plant</TH>
                  <TH>Sender</TH>
                  <TH>From</TH>
                  <TH>Status</TH>
                  <TH>Last test</TH>
                  {manage && <TH>Action</TH>}
                </tr>
              </THead>
              <TBody>
                {plants.map((p) => {
                  const c = byPlant.get(p.id) ?? null;
                  return (
                    <TR key={p.id}>
                      <TD>
                        <span className="font-medium">{p.name}</span>{" "}
                        <span className="text-ink-muted">({p.plantCode})</span>
                      </TD>
                      <TD>{c ? DRIVER_SHORT[c.driver] : "—"}</TD>
                      <TD className="text-ink-secondary">
                        {c
                          ? c.fromName
                            ? `${c.fromName} <${c.fromAddress}>`
                            : c.fromAddress
                          : "—"}
                      </TD>
                      <TD>
                        {!c ? (
                          <Badge tone="danger">Not set up</Badge>
                        ) : c.isActive ? (
                          <Badge tone="success">On</Badge>
                        ) : (
                          <Badge tone="warning">Switched off</Badge>
                        )}
                      </TD>
                      <TD>
                        <LastTest c={c} />
                      </TD>
                      {manage && (
                        <TD align="right">
                          <div className="flex justify-end gap-1">
                            {c ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setTesting(c)}
                                >
                                  <Send className="h-3.5 w-3.5" /> Send test
                                </Button>
                                <Tooltip content="Edit">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={`Edit ${p.name} mail settings`}
                                    onClick={() =>
                                      setEditing({ plant: p, config: c })
                                    }
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                                <Tooltip content="Remove">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={`Remove ${p.name} mail settings`}
                                    onClick={() => setDeleting(c)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() =>
                                  setEditing({ plant: p, config: null })
                                }
                              >
                                Set up mail
                              </Button>
                            )}
                          </div>
                        </TD>
                      )}
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {editing && (
        <Editor
          plant={editing.plant}
          config={editing.config}
          onClose={() => setEditing(null)}
        />
      )}
      {testing && (
        <TestDialog config={testing} onClose={() => setTesting(null)} />
      )}
      <ConfirmDialog
        open={!!deleting}
        title={`Remove ${deleting?.plantName ?? ""} mail settings?`}
        description="The plant will not be able to send any mail (invites, login codes, arrival e-mails) until mail is set up again."
        confirmLabel="Remove"
        danger
        onConfirm={() => deleting && remove.mutateAsync(deleting)}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function LastTest({ c }: { c: MailConfigResponse | null }) {
  if (!c?.lastTestAt) return <span className="text-ink-muted">Not tested</span>;
  return c.lastTestOk ? (
    <span className="inline-flex items-center gap-1 text-success-text">
      <CheckCircle2 className="h-4 w-4" /> {formatDateTime(c.lastTestAt)}
    </span>
  ) : (
    <Tooltip content={c.lastTestError ?? "Failed"}>
      <span className="inline-flex items-center gap-1 text-danger">
        <XCircle className="h-4 w-4" /> Failed · {formatDateTime(c.lastTestAt)}
      </span>
    </Tooltip>
  );
}

interface FormState {
  driver: MailDriver;
  fromName: string;
  fromAddress: string;
  replyTo: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean;
  smtpUser: string;
  oauthClientId: string;
  zohoRegion: ZohoRegion;
  zohoAccountId: string;
  smtpPassword: string;
  oauthClientSecret: string;
  oauthRefreshToken: string;
  isActive: boolean;
}

const FIELDS = [
  "driver",
  "fromName",
  "fromAddress",
  "replyTo",
  "smtpHost",
  "smtpPort",
  "smtpUser",
  "oauthClientId",
  "zohoAccountId",
  "smtpPassword",
  "oauthClientSecret",
  "oauthRefreshToken",
];

function initial(c: MailConfigResponse | null): FormState {
  return {
    driver: c?.driver ?? "zoho_api",
    fromName: c?.fromName ?? "",
    fromAddress: c?.fromAddress ?? "",
    replyTo: c?.replyTo ?? "",
    smtpHost: c?.smtpHost ?? "",
    smtpPort: c?.smtpPort ? String(c.smtpPort) : "",
    smtpSecure: c?.smtpSecure ?? true,
    smtpUser: c?.smtpUser ?? "",
    oauthClientId: c?.oauthClientId ?? "",
    zohoRegion: c?.zohoRegion ?? "in",
    zohoAccountId: c?.zohoAccountId ?? "",
    smtpPassword: "",
    oauthClientSecret: "",
    oauthRefreshToken: "",
    isActive: c?.isActive ?? true,
  };
}

function Editor({
  plant,
  config,
  onClose,
}: {
  plant: Facility;
  config: MailConfigResponse | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState<FormState>(() => initial(config));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };
  // A secret is "saved" only while the driver is the one it was saved for.
  const sameDriver = config?.driver === f.driver;
  const saved = {
    smtpPassword: sameDriver && !!config?.hasSmtpPassword,
    oauthClientSecret: sameDriver && !!config?.hasOauthClientSecret,
    oauthRefreshToken: sameDriver && !!config?.hasOauthRefreshToken,
  };

  const save = useMutation({
    mutationFn: () =>
      api<MailConfigResponse>(
        config
          ? `/masters/mail-settings/${config.id}`
          : "/masters/mail-settings",
        {
          method: config ? "PATCH" : "POST",
          json: {
            facilityId: plant.id,
            driver: f.driver,
            fromName: f.fromName,
            fromAddress: f.fromAddress.trim(),
            replyTo: f.replyTo.trim(),
            smtpHost: f.smtpHost,
            smtpPort: f.smtpPort ? Number(f.smtpPort) : null,
            smtpSecure: f.smtpSecure,
            smtpUser: f.smtpUser,
            oauthClientId: f.oauthClientId,
            zohoRegion: f.zohoRegion,
            zohoAccountId: f.zohoAccountId,
            smtpPassword: f.smtpPassword,
            oauthClientSecret: f.oauthClientSecret,
            oauthRefreshToken: f.oauthRefreshToken,
            isActive: f.isActive,
          },
        }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success(
        `${plant.name} mail ${config ? "updated" : "set up"}. Send a test mail to check it.`
      );
      onClose();
    },
    onError: (e) =>
      applyApiError(
        e,
        (name, msg) => setErrors((x) => ({ ...x, [name]: msg })),
        FIELDS
      ),
  });

  const api_ = f.driver !== "smtp";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {config ? "Edit" : "Set up"} mail · {plant.name}
          </DialogTitle>
          <DialogDescription>
            Every mail {plant.name}'s work sends goes out from this account.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Send with" required error={errors.driver}>
            <ToggleGroup
              type="single"
              value={f.driver}
              onValueChange={(v) => v && set("driver", v as MailDriver)}
              aria-label="Send with"
            >
              {(Object.keys(MAIL_DRIVER_LABELS) as MailDriver[]).map((d) => (
                <ToggleGroupItem key={d} value={d}>
                  {MAIL_DRIVER_LABELS[d]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="From name"
              htmlFor="ms-from-name"
              error={errors.fromName}
            >
              <Input
                id="ms-from-name"
                value={f.fromName}
                placeholder={`${plant.name} Plant`}
                onChange={(e) => set("fromName", e.target.value)}
              />
            </Field>
            <Field
              label="From e-mail address"
              htmlFor="ms-from"
              required
              error={errors.fromAddress}
            >
              <Input
                id="ms-from"
                type="email"
                value={f.fromAddress}
                invalid={!!errors.fromAddress}
                onChange={(e) => set("fromAddress", e.target.value)}
              />
            </Field>
          </div>

          {f.driver === "zoho_api" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Zoho data centre" htmlFor="ms-region" required>
                <Select
                  id="ms-region"
                  value={f.zohoRegion}
                  onChange={(e) =>
                    set("zohoRegion", e.target.value as ZohoRegion)
                  }
                >
                  {ZOHO_REGION_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {ZOHO_REGIONS[k].label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Account ID"
                htmlFor="ms-zoho-account"
                description="Optional; found from the From address when blank."
                error={errors.zohoAccountId}
              >
                <Input
                  id="ms-zoho-account"
                  value={f.zohoAccountId}
                  onChange={(e) => set("zohoAccountId", e.target.value)}
                />
              </Field>
            </div>
          )}

          {f.driver !== "zoho_api" && (
            <Field
              label="Reply-to"
              htmlFor="ms-reply"
              description="Optional; where replies go if not the From address."
              error={errors.replyTo}
            >
              <Input
                id="ms-reply"
                type="email"
                value={f.replyTo}
                onChange={(e) => set("replyTo", e.target.value)}
              />
            </Field>
          )}

          {api_ ? (
            <>
              <Field
                label="OAuth client ID"
                htmlFor="ms-client-id"
                required
                error={errors.oauthClientId}
                description={
                  f.driver === "zoho_api"
                    ? "A Self Client from api-console.zoho.in with scopes ZohoMail.messages.CREATE and ZohoMail.accounts.READ."
                    : "A Google Cloud OAuth client with the Gmail API enabled and the gmail.send scope."
                }
              >
                <Input
                  id="ms-client-id"
                  value={f.oauthClientId}
                  invalid={!!errors.oauthClientId}
                  onChange={(e) => set("oauthClientId", e.target.value)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <SecretField
                  id="ms-client-secret"
                  label="OAuth client secret"
                  value={f.oauthClientSecret}
                  saved={saved.oauthClientSecret}
                  error={errors.oauthClientSecret}
                  onChange={(v) => set("oauthClientSecret", v)}
                />
                <SecretField
                  id="ms-refresh"
                  label="Refresh token"
                  value={f.oauthRefreshToken}
                  saved={saved.oauthRefreshToken}
                  error={errors.oauthRefreshToken}
                  onChange={(v) => set("oauthRefreshToken", v)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                Fill in for:
                {Object.values(SMTP_PRESETS).map((p) => (
                  <Button
                    key={p.label}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      set("smtpHost", p.host);
                      set("smtpPort", String(p.port));
                      set("smtpSecure", p.secure);
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_7rem]">
                <Field
                  label="SMTP server"
                  htmlFor="ms-host"
                  required
                  error={errors.smtpHost}
                >
                  <Input
                    id="ms-host"
                    value={f.smtpHost}
                    invalid={!!errors.smtpHost}
                    onChange={(e) => set("smtpHost", e.target.value)}
                  />
                </Field>
                <Field
                  label="Port"
                  htmlFor="ms-port"
                  required
                  error={errors.smtpPort}
                >
                  <Input
                    id="ms-port"
                    inputMode="numeric"
                    value={f.smtpPort}
                    invalid={!!errors.smtpPort}
                    onChange={(e) =>
                      set("smtpPort", e.target.value.replace(/\D/g, ""))
                    }
                  />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={f.smtpSecure}
                  onCheckedChange={(v) => set("smtpSecure", v)}
                  aria-label="SSL from the start"
                />
                SSL from the start (port 465); off = STARTTLS (port 587)
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="User name"
                  htmlFor="ms-user"
                  required
                  error={errors.smtpUser}
                >
                  <Input
                    id="ms-user"
                    value={f.smtpUser}
                    invalid={!!errors.smtpUser}
                    onChange={(e) => set("smtpUser", e.target.value)}
                  />
                </Field>
                <SecretField
                  id="ms-pass"
                  label="Password / app password"
                  value={f.smtpPassword}
                  saved={saved.smtpPassword}
                  error={errors.smtpPassword}
                  onChange={(v) => set("smtpPassword", v)}
                />
              </div>
            </>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={f.isActive}
              onCheckedChange={(v) => set("isActive", v)}
              aria-label="Mail on"
            />
            Mail on: when off, this plant's mail is refused.
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Write-only: a stored value is never shown, only that one is there. Blank keeps it. */
function SecretField(p: {
  id: string;
  label: string;
  value: string;
  saved: boolean;
  error?: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <Field
      label={p.label}
      htmlFor={p.id}
      required={!p.saved}
      error={p.error}
      description={
        p.saved
          ? "Saved. Leave blank to keep it, or type a new one."
          : undefined
      }
    >
      <Input
        id={p.id}
        type="password"
        autoComplete="new-password"
        value={p.value}
        placeholder={p.saved ? "•••••••• saved" : ""}
        invalid={!!p.error}
        onChange={(e) => p.onChange(e.target.value)}
      />
    </Field>
  );
}

function TestDialog({
  config,
  onClose,
}: {
  config: MailConfigResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const me = useAuth((s) => s.me);
  const [to, setTo] = useState(me?.email ?? "");
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; to: string; error: string | null }>(
        `/masters/mail-settings/${config.id}/test`,
        { method: "POST", json: to.trim() ? { to: to.trim() } : {} }
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : "Could not send the test"
      ),
  });
  const r = test.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Send a test mail · {config.plantName}</DialogTitle>
          <DialogDescription>
            Sent from {config.fromAddress} through{" "}
            {MAIL_DRIVER_LABELS[config.driver]}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Send to" htmlFor="ms-test-to" required>
            <Input
              id="ms-test-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          {r &&
            (r.ok ? (
              <Callout tone="success" title="Sent">
                Check {r.to}'s inbox (and spam folder).
              </Callout>
            ) : (
              <Callout tone="danger" title="Not sent">
                <span className="break-words">{r.error}</span>
              </Callout>
            ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            loading={test.isPending}
            disabled={!to.trim()}
            onClick={() => test.mutate()}
          >
            <Send className="h-4 w-4" /> Send test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
